// Third-person orbit camera: follows the predicted self pose, oriented by
// the orbit state that core/input maintains. Poses arrive already in
// render space (world minus the area's floating origin); `groundY` lifts
// the rig to the terrain under the avatar.

import { PLAYER_HEIGHT, type PlayerPose } from "@bebop/shared";
import * as THREE from "three";
import type { OrbitState } from "../core/input";

const AVATAR_EYE_HEIGHT = PLAYER_HEIGHT * 0.72;

export class ThirdPersonCamera {
  readonly camera: THREE.PerspectiveCamera;
  private target = new THREE.Vector3();

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(70, aspect, 0.1, 100);
    this.camera.position.set(0, 3, 9);
  }

  update(pose: PlayerPose | undefined, orbit: OrbitState, groundY = 0): void {
    if (!pose) return;
    const eyeY = groundY + AVATAR_EYE_HEIGHT;
    this.target.set(pose.x, eyeY, pose.z);
    const horizontal = Math.cos(orbit.pitch) * orbit.distance;
    this.camera.position.set(
      pose.x + Math.sin(orbit.yaw) * horizontal,
      eyeY + Math.sin(orbit.pitch) * orbit.distance,
      pose.z + Math.cos(orbit.yaw) * horizontal,
    );
    this.camera.lookAt(this.target);
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Areas pick their own horizon (clubs 100 m, the surface ~2.6 km). */
  setFar(far: number): void {
    if (this.camera.far === far) return;
    this.camera.far = far;
    this.camera.updateProjectionMatrix();
  }

  /** Areas may pick their own lens (the Black shoots long — see AreaScene.fov). */
  setFov(fov: number): void {
    if (this.camera.fov === fov) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }
}
